import { supabase } from "@/integrations/supabase/client";

export type LogLevel = "info" | "warning" | "error" | "critical";
export type LogCategory = "auth" | "user" | "payment" | "system" | "api" | "integration" | "luma" | "other";

interface LogInput {
  level?: LogLevel;
  category?: LogCategory;
  event: string;
  message?: string;
  payload?: Record<string, unknown>;
  userId?: string | null;
  userEmail?: string | null;
}

// Mask sensitive fields in payloads to keep logs safe.
const SENSITIVE_KEYS = /password|token|secret|apikey|api_key|authorization|cpf|cnpj|pix|card/i;
function sanitize(value: unknown, depth = 0): unknown {
  if (value === null || value === undefined) return value;
  if (depth > 4) return "[Truncated]";
  if (Array.isArray(value)) return value.slice(0, 20).map((v) => sanitize(v, depth + 1));
  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (SENSITIVE_KEYS.test(k)) {
        out[k] = "[REDACTED]";
      } else {
        out[k] = sanitize(v, depth + 1);
      }
    }
    return out;
  }
  if (typeof value === "string" && value.length > 1000) return value.slice(0, 1000) + "…";
  return value;
}

// ---------------- Rate-limit / dedup / batching ----------------
const MAX_LOGS_PER_MINUTE = 30;
const BATCH_FLUSH_MS = 5000;
const BATCH_MAX_SIZE = 25;
const DEDUPE_WINDOW_MS = 10_000;

type QueueItem = {
  level: LogLevel;
  source: "frontend";
  category: LogCategory;
  event: string;
  message: string;
  user_id: string | null;
  user_email: string | null;
  payload: any;
  _key: string;
};

let queue: QueueItem[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let windowStart = Date.now();
let windowCount = 0;
const recentKeys = new Map<string, number>(); // key -> last seen ts

function rateLimitOk(): boolean {
  const now = Date.now();
  if (now - windowStart > 60_000) {
    windowStart = now;
    windowCount = 0;
  }
  if (windowCount >= MAX_LOGS_PER_MINUTE) return false;
  windowCount += 1;
  return true;
}

function isDuplicate(key: string): boolean {
  const now = Date.now();
  // GC old entries occasionally
  if (recentKeys.size > 200) {
    for (const [k, ts] of recentKeys) {
      if (now - ts > DEDUPE_WINDOW_MS) recentKeys.delete(k);
    }
  }
  const last = recentKeys.get(key);
  recentKeys.set(key, now);
  return last !== undefined && now - last < DEDUPE_WINDOW_MS;
}

async function flush() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (queue.length === 0) return;
  const batch = queue.splice(0, BATCH_MAX_SIZE);
  const rows = batch.map(({ _key, ...row }) => row);
  try {
    await supabase.from("system_logs").insert(rows);
  } catch (err) {
    console.warn("[systemLogger] batch insert failed", err);
  }
  if (queue.length > 0) scheduleFlush();
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => flush().catch(() => {}), BATCH_FLUSH_MS);
}

export async function logEvent(input: LogInput): Promise<void> {
  try {
    const event = input.event;
    const message = input.message ?? "";
    const key = `${input.level ?? "info"}|${input.category ?? "other"}|${event}|${message}`;

    if (isDuplicate(key)) return;
    if (!rateLimitOk()) return;

    let userId = input.userId ?? null;
    let userEmail = input.userEmail ?? null;
    if (userId === null) {
      try {
        const { data } = await supabase.auth.getUser();
        userId = data.user?.id ?? null;
        userEmail = data.user?.email ?? null;
      } catch { /* ignore */ }
    }

    queue.push({
      level: input.level ?? "info",
      source: "frontend",
      category: input.category ?? "other",
      event,
      message,
      user_id: userId,
      user_email: userEmail,
      payload: sanitize(input.payload ?? {}) as any,
      _key: key,
    });

    if (queue.length >= BATCH_MAX_SIZE) {
      flush().catch(() => {});
    } else {
      scheduleFlush();
    }
  } catch (err) {
    console.warn("[systemLogger] failed to enqueue log", err);
  }
}

// Convenience helpers
export const logInfo = (event: string, message?: string, payload?: Record<string, unknown>, category: LogCategory = "other") =>
  logEvent({ level: "info", category, event, message, payload });

export const logWarning = (event: string, message?: string, payload?: Record<string, unknown>, category: LogCategory = "other") =>
  logEvent({ level: "warning", category, event, message, payload });

export const logError = (event: string, message?: string, payload?: Record<string, unknown>, category: LogCategory = "other") =>
  logEvent({ level: "error", category, event, message, payload });

export const logCritical = (event: string, message?: string, payload?: Record<string, unknown>, category: LogCategory = "other") =>
  logEvent({ level: "critical", category, event, message, payload });

// Install global handlers once (browser)
let installed = false;
export function installGlobalErrorLogging() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (e) => {
    logError(
      "frontend_uncaught_error",
      e.message || "Uncaught error",
      {
        filename: e.filename,
        lineno: e.lineno,
        colno: e.colno,
        stack: e.error?.stack,
      },
      "system",
    );
  });

  window.addEventListener("unhandledrejection", (e) => {
    const reason = e.reason;
    const message = reason?.message ?? String(reason);
    logError(
      "frontend_unhandled_rejection",
      message,
      { stack: reason?.stack },
      "system",
    );
  });

  // Best-effort flush on unload
  window.addEventListener("beforeunload", () => {
    flush().catch(() => {});
  });
}
